/**
 * The message that starts the post-onboarding Flow conversation.
 *
 * The backend only injects the onboarding directive while a TURN is running
 * (`append_onboarding_directive_if_needed`, gated on wizard-seeded memory plus
 * no other conversation). Opening the chat panel runs no turn, so Flow sat
 * there silent — the athlete finished onboarding and met an empty box.
 *
 * iOS has always sent this as a plain user message
 * (`MainTabView.aiChatKickoffMessage`), which is what makes Flow speak first.
 * The wording matches iOS deliberately: the onboarding skill tells Flow the
 * first message may be an automatic kickoff and not to quote it back, so a
 * divergent string here would read as something the athlete typed.
 */
export const FLOW_ONBOARDING_KICKOFF = 'Just finished setting up my profile.'

/**
 * The pending kickoff, in a place a remount cannot eat.
 *
 * The intent used to travel ONLY as `location.state`, which any redirect
 * silently discards. Measured on staging 2026-09-15 (PostHog distinct_id
 * staging:65), four pageviews inside 250ms at the end of a walk:
 *
 *   11:45:34.438  /calendar            <- completion navigate, state intact
 *   11:45:34.472  /onboarding          <- AuthenticatedAccessGate bounced it
 *   11:45:34.506  /onboarding/welcome  <- restarted at stage 1
 *   11:45:34.684  /calendar            <- arrived again, STATE GONE
 *
 * The athlete ends on /calendar so it looks right, but that second arrival
 * carries no state, `openFlow` is false and Flow never opens. The bounce
 * itself is fixed at its cause (the profile cache is marked complete in the
 * same batch as the navigate), and this exists so that a single lost redirect
 * can never silently cost the kickoff again.
 *
 * sessionStorage rather than `flowChatBus`: the bus needs a live subscriber at
 * the instant it fires, and FlowChatLauncher REMOUNTS across the route change
 * — which is the very race being fixed. A stored intent survives remounts,
 * redirects and a reload, and is consumed exactly once.
 *
 * Per TAB, not per browser: a second tab must not inherit someone's kickoff.
 */
const KICKOFF_KEY = 'beoflow.flow-kickoff'

/** A kickoff is about the minutes just after onboarding. Without a bound, one
 *  stored while the athlete happened to be on a Flow-suppressed route could sit
 *  in the tab for an hour and then announce "Just finished setting up my
 *  profile." on some unrelated page, which reads as the app talking to itself.
 *  Cross-model review, 2026-09-15. */
const KICKOFF_TTL_MS = 10 * 60 * 1000

interface StoredKickoff { message: string; at: number }

export function rememberFlowKickoff(message: string): void {
  const value: StoredKickoff = { message, at: Date.now() }
  try { sessionStorage.setItem(KICKOFF_KEY, JSON.stringify(value)) } catch { /* storage unavailable */ }
}

/**
 * Reads WITHOUT clearing.
 *
 * Reading and clearing together was the obvious design and it was wrong,
 * measured 2026-09-15: the first mount consumed the kickoff, the tree then
 * remounted (the route is replaced with no state, and StrictMode remounts in
 * development anyway), and the mount that survived found an empty slot. Flow
 * stayed shut with the intent already thrown away.
 *
 * So the value lives until somebody can actually ACT on it. Clearing is the
 * job of `clearFlowKickoff`, called at the moment the message is sent or
 * deliberately declined.
 */
export function peekFlowKickoff(): string | null {
  try {
    const raw = sessionStorage.getItem(KICKOFF_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<StoredKickoff> | null
    if (!parsed || typeof parsed.message !== 'string' || typeof parsed.at !== 'number') return null
    if (Date.now() - parsed.at > KICKOFF_TTL_MS) {
      sessionStorage.removeItem(KICKOFF_KEY)
      return null
    }
    return parsed.message || null
  } catch {
    return null
  }
}

/** Clear it once it has been sent, or once it has been decided against, so the
 *  same sentence is never put in the athlete's mouth twice. */
export function clearFlowKickoff(): void {
  try { sessionStorage.removeItem(KICKOFF_KEY) } catch { /* storage unavailable */ }
}
